<script setup>
	import { computed, onBeforeMount, ref, onMounted, watch } from 'vue';
	import { RouterView, useRoute, useRouter } from 'vue-router';
	import { useMapStoreData, useScreensaverStore, useAnalyticsStore, useCategoriesStore } from '@/stores/index';
	import { useI18n } from 'vue-i18n';
	import PoisFilters from '@/components/Poisfilters.vue';
	import FavoritesList from '@/components/FavoritesList.vue';
	import LanguageChanger from './components/LanguageChanger.vue';
	import MapView from '@/views/MapView.vue';
	import Screensaver from '@/components/Screensaver.vue';
	import printingTemplate from '@/components/printing/printingTemplate.vue';
	import offlineTag from '@/components/OfflineTag.vue';
	import ptujBranding from '@/components/branding/PtujBranding.vue';
	import horizontalBlocker from '@/components/HorizontalBlocker.vue';

	const mapStore = useMapStoreData();
	const route = useRoute();
	const router = useRouter();
	const screensaverStore = useScreensaverStore();
	const analyticsStore = useAnalyticsStore();
	const { t } = useI18n();
	const stayingActiveCounter = ref(0);
	const pathRoute = computed(() => route.path !== '/scenarios/');
	// const whishingWellRoute = computed(() => route.path == '/wishingWell');
	// Offline banner uses mapStore.isOnline (same signal as map bootstrap).
	const isIframeRoute = computed(() => route.name === 'iframe');
	let isPortrait = ref(true);
	const appClickListener = () => {
		screensaverStore.resetScreensaverTimer();
	};

	onMounted(() => {
		if (mapStore.defaultRoute == '/map/') mapStore.showMap = true;
		router.push(mapStore.defaultRoute);
		screensaverStore.resetScreensaverTimer();

		window.addEventListener('resize', checkOrientation);
	});

	function checkOrientation() {
		if (mapStore.isOnLocation) return;
		if (window.matchMedia('(orientation: portrait)').matches || window.innerWidth > 833) {
			isPortrait.value = true;
		} else {
			isPortrait.value = false;
		}
	}

	onBeforeMount(() => {
		//analyticsStore.setupAnalytics();
		analyticsStore.startSession();
		screensaverStore.router = router;
		useCategoriesStore().translator = t;

		setInterval(
			() => {
				stayingActiveCounter.value = stayingActiveCounter.value + 1;
			},
			5 * 60 * 1000
		); // every 5 minutes

		// Check the initial online status — keep map store in sync with the browser.
		mapStore.isOnline = navigator.onLine;
		if (navigator.onLine) {
			console.log('You are online!');
		} else {
			console.log('You are offline!');
		}

		// Listen for online status changes
		window.addEventListener('online', function () {
			mapStore.isOnline = true;
			console.log('You are back online!');
		});

		window.addEventListener('offline', function () {
			mapStore.isOnline = false;
			console.log('You have lost internet connection.');
		});
	});

	// async function inspectIndexedDB() {
	// 	if (!('indexedDB' in window)) {
	// 		console.log('This browser does not support IndexedDB.');
	// 		return;
	// 	}

	// 	try {
	// 		navigator.storage.estimate().then((estimate) => {
	// 			console.log('estimate: ', estimate);
	// 			console.log(`Quota: ${estimate.quota}, Usage: ${estimate.usage}`);
	// 		});

	// 		const databases = await indexedDB.databases();
	// 		console.log('IndexedDB Databases:');

	// 		if (databases.length === 0) {
	// 			console.log('No databases found.');
	// 			return;
	// 		}

	// 		for (const dbInfo of databases) {
	// 			const dbName = dbInfo.name;
	// 			const dbVersion = dbInfo.version;

	// 			console.log(`\nDatabase: ${dbName}, Version: ${dbVersion}`);

	// 			const request = indexedDB.open(dbName, dbVersion);

	// 			request.onsuccess = (event) => {
	// 				const db = event.target.result;
	// 				const objectStoreNames = db.objectStoreNames;

	// 				console.log(`  Object Stores:`);
	// 				for (const storeName of objectStoreNames) {
	// 					const transaction = db.transaction(storeName, 'readonly');
	// 					const store = transaction.objectStore(storeName);

	// 					// Count the number of items in the store
	// 					const countRequest = store.count();
	// 					countRequest.onsuccess = () => {
	// 						const itemCount = countRequest.result;
	// 						console.log(`    - ${storeName} (Length: ${itemCount} items)`);

	// 						// Fetch all keys from the store
	// 						const getAllKeysRequest = store.getAllKeys();
	// 						getAllKeysRequest.onsuccess = (event) => {
	// 							console.log(`      Keys in ${storeName}:`);
	// 							event.target.result.forEach((key, index) => {
	// 								console.log(`        ${index + 1}: ${key}`);
	// 							});
	// 						};

	// 						getAllKeysRequest.onerror = (event) => {
	// 							console.error(`      Error fetching keys from ${storeName}:`, event.target.error.message);
	// 						};
	// 					};

	// 					countRequest.onerror = (event) => {
	// 						console.error(`    - ${storeName}: Error counting items: ${event.target.error.message}`);
	// 					};
	// 				}

	// 				db.close();
	// 			};

	// 			request.onerror = (event) => {
	// 				console.error(`  Error opening database ${dbName}: ${event.target.error.message}`);
	// 			};
	// 		}
	// 	} catch (error) {
	// 		console.error('Error inspecting IndexedDB:', error);
	// 	}
	// }

	watch(
		() => mapStore.isOnLocation,
		(newValue) => {
			if (!newValue) {
				checkOrientation();
			}
		}
	);
</script>

<template>
	<div :class="'destination-' + useMapStoreData().destinationId" class="app-container">
		<!--<button style="z-index: 999999; position:absolute; top:10px; right:10px" @click="inspectIndexedDB()">Show IndexedDB</button>-->
		<horizontalBlocker v-if="!useMapStoreData().isOnLocation && !isPortrait"></horizontalBlocker>
		<Screensaver v-if="mapStore.appReady" v-show="screensaverStore.showSreensaver"></Screensaver>
		<div class="app-clicklistener" @click="appClickListener()">
			<LanguageChanger
				uniqueClass="screensaver-lang"
				class="language-switcher-screensaver"
				v-show="screensaverStore.showSreensaver"
			></LanguageChanger>
			<RouterView v-slot="{ Component }">
				<Transition v-if="!isIframeRoute" name="fade" mode="out-in">
					<component :is="Component" :key="$route.fullPath" />
				</Transition>
				<Transition name="iframe-transition" v-else>
					<component :is="Component" :key="$route.fullPath" />
				</Transition>
			</RouterView>
			<PoisFilters v-show="(mapStore.showMap || !pathRoute) && !mapStore.isMapCopyMode" />
			<FavoritesList v-show="(mapStore.showMap || !pathRoute) && (!mapStore.isMapCopyMode || mapStore.mapCopyUseFavorites)" />
			<MapView v-show="mapStore.appReady"></MapView>
			<printingTemplate v-if="useMapStoreData().destinationId == '1'"></printingTemplate>
			<offlineTag v-if="!mapStore.isOnline"></offlineTag>
			<ptujBranding v-if="useMapStoreData().destinationId == '17'"></ptujBranding>
			<!-- <WishingWellAnim v-if="whishingWellRoute"></WishingWellAnim> -->
		</div>
		<p class="stayingActiveCounter">{{ stayingActiveCounter }}</p>
	</div>
</template>

<style scoped lang="scss">
	@import 'assets/style/mixins.scss';
	.app-container {
		background-color: #fff;
	}

	.language-switcher-screensaver {
		position: absolute;
		bottom: 2.5vw;
		left: 2.5vw;
	}

	.stayingActiveCounter {
		position: absolute;
		top: -999999999vh;
	}

	@media only screen and (orientation: portrait) {
		.language-switcher-screensaver {
			position: absolute;
			bottom: 50%;
			left: 2.5vw;
		}
	}

	.iframe-transition-enter-active,
	.iframe-transition-leave-active {
		transition: opacity 0.3s ease;
	}
	.iframe-transition-enter-from,
	.iframe-transition-leave-to {
		opacity: 0;
	}
</style>
